import { createSelector } from '@ngrx/store';

import { UserSelectors } from '@core/account-data';
import { CandidateSelectors } from '@core/candidate-data';
import type { Scope } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import type { Task, TaskRequestParams } from '@core/tasks-types';
import { sortByKey } from '@core/utils-legacy';

import type { PaginatedTasksBackendResponse } from './tasks-http.service';
import type { TaskCreatePayload, TaskPayload } from '../../types';
import { EntityStoreKey, TasksDataScope } from '../../types';

export const candidateTaskScope = (candidateId: number) =>
  scopeFactory(TasksDataScope.Candidate, candidateId);

type TasksMeta = { scopeMeta: { counters: { completed: number; uncompleted: number } } };

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Task, TasksMeta>()(
  EntityStoreKey.Tasks,
  {
    customActions: adapter => {
      const selectId = ({ id }: Task) => id;
      const defaultActions = getEntityCollectionActions(adapter, selectId);

      return {
        fetchCandidateTasks: (state, payload: { data: { candidateId: number }; scope?: Scope }) =>
          adapter.markAsPending(state, {
            scope: candidateTaskScope(payload.data.candidateId),
            entities: [],
          }),
        fetchCandidateTasksSuccess: (state, res: { tasks: Task[]; candidateId: number }) =>
          pipeState(
            adapter.addMany(state, res.tasks, {
              scope: candidateTaskScope(res.candidateId),
            }),
            next => adapter.markAsLoaded(next),
          ),
        fetchTasksCollection: (
          state,
          payload: { params: Partial<TaskRequestParams>; scope: string; clearScope?: boolean },
        ) => defaultActions.fetchCollection(state, payload),
        fetchTasksCollectionSuccess: (
          state,
          payload: { data: PaginatedTasksBackendResponse; scope: string; clearScope?: boolean },
        ) =>
          pipeState(
            state,
            next =>
              adapter.setScopeMetaCustom(
                next,
                { counters: payload.data?.meta?.counters },
                { scope: payload.scope },
              ),
            next =>
              payload.clearScope
                ? adapter.setMany(next, payload.data.tasks, { scope: payload.scope })
                : adapter.addMany(next, payload.data.tasks, { scope: payload.scope }),
            next => adapter.markAsLoaded(next, { scope: payload.scope, entities: [] }),
          ),
        fetchCollectionFailed: (state, payload) =>
          defaultActions.fetchCollectionFailed(state, payload),
        createTask: (state, payload: { data: TaskCreatePayload; scope: string }) =>
          adapter.markAsPending(state),
        createTaskSuccess: (state, payload: { data: Task; scope: string }) =>
          pipeState(
            state,
            next => adapter.addOne(next, payload.data, { scope: payload.scope }),
            next => adapter.markAsLoaded(next, { scope: payload.scope, entities: [] }),
          ),
        createTaskFailed: ngrxEvent<{
          error: { errors: Array<{ code: string; data: {}; message: string }> };
        }>(),
        updateTask: (state, payload: { data: Partial<TaskPayload>; scope?: string }) =>
          adapter.markAsPending(state),
        updateTaskSuccess: (state, payload: { data: Task; scope?: string }) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next, { scope: payload?.scope || 'main', entities: [] }),
          ),
        updateTaskFailed: ngrxEvent<{
          error: { errors: Array<{ code: string; data: {}; message: string }> };
        }>(),
      };
    },
    references: makeRef => [
      makeRef('adminIds', 'admins', UserSelectors.byIds),
      makeRef('candidateId', 'candidate', candidateId => CandidateSelectors.byId(candidateId)),
    ],
  },
);

const selectCandidateTasks = memoizeSelector((candidateId: number) => {
  const candidateTasksWithReferencesSelector = selectors.entitiesWithReferences({
    scope: candidateTaskScope(candidateId),
  });
  return createSelector(candidateTasksWithReferencesSelector, (tasks: Task[]) =>
    tasks.sort(sortByKey('createdAt')),
  );
});
const selectCandidateTasksLoaded = memoizeSelector((candidateId: number) =>
  createSelector(
    selectors.isLoaded({ scope: candidateTaskScope(candidateId) }),
    (loaded: boolean) => loaded,
  ),
);

const tasksSelectors = { ...selectors, selectCandidateTasks, selectCandidateTasksLoaded };

export {
  actions as TasksActions,
  reducer as TasksReducer,
  tasksSelectors,
  namespace as TasksNamespace,
};
